/**
 * Global search in the top bar: debounced /api/search over tools, runs and
 * files. Selecting a hit navigates (tools → single-tool page, runs → their
 * page context) or downloads (files). Fully keyboard-driven: arrows +
 * Enter + Escape over a proper combobox/listbox pair; stale responses are
 * aborted so a slow earlier request can never overwrite a newer one.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Popper,
  TextField,
  ClickAwayListener,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from "@mui/icons-material/Handyman";
import PlayIcon from "@mui/icons-material/PlayCircleOutline";
import FileIcon from "@mui/icons-material/DescriptionOutlined";
import { api, type SearchResults } from "../lib/api";
import { resolveText } from "../lib/tooltext";
import {
  DATE_SLICE_LEN,
  ID_SLICE_LEN,
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_CHARS,
} from "../lib/config";

const LISTBOX_ID = "global-search-listbox";

type SearchItem = {
  id: string;
  group: "tools" | "jobs" | "files";
  icon: ReactNode;
  primary: string;
  secondary: string;
  action: () => void;
};

export function GlobalSearch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < SEARCH_MIN_CHARS) {
      setResults(null);
      setBusy(false); // a shrinking query must not leave the spinner on
      return;
    }
    setBusy(true);
    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      api
        .search(q.trim(), controller.signal)
        .then((next) => {
          setResults(next);
          setBusy(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return; // superseded — newer query owns the UI
          setResults(null);
          setBusy(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [q]);

  const close = useCallback(() => {
    setResults(null);
    setQ("");
    setActiveIndex(-1);
  }, []);

  const items = useMemo<SearchItem[]>(() => {
    if (!results) return [];
    const list: SearchItem[] = [];
    for (const tool of results.tools) {
      list.push({
        id: `gs-tool-${tool.key}`,
        group: "tools",
        icon: <BuildIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />,
        primary: resolveText(tool.display_name, i18n.language),
        secondary: tool.key,
        action: () => {
          void navigate(`/tool?select=${encodeURIComponent(tool.key)}`);
          close();
        },
      });
    }
    for (const job of results.jobs) {
      list.push({
        id: `gs-job-${job.job_id}`,
        group: "jobs",
        icon: <PlayIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />,
        primary: `${job.job_id.slice(0, ID_SLICE_LEN)} · ${job.dag}`,
        secondary: `${t(`states.${job.state}`, { defaultValue: job.state })}${job.created_at ? ` · ${job.created_at.slice(0, DATE_SLICE_LEN)}` : ""}`,
        action: () => {
          void navigate(`/runs/${encodeURIComponent(job.job_id)}`);
          close();
        },
      });
    }
    for (const file of results.files) {
      list.push({
        id: `gs-file-${file.artifact_id}`,
        group: "files",
        icon: <FileIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />,
        primary: file.display_name,
        secondary: file.created_at ? file.created_at.slice(0, DATE_SLICE_LEN) : file.kind,
        action: () => {
          // authenticated fetch-and-save — a navigation has no bearer token
          void api.downloadExcel(file.artifact_id, file.display_name);
          close();
        },
      });
    }
    return list;
  }, [results, navigate, close, t, i18n.language]);

  // New result set → nothing highlighted yet.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  const empty = results !== null && items.length === 0;
  const openList = Boolean(results);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (openList) {
        e.preventDefault();
        setResults(null);
        setActiveIndex(-1);
      }
      return;
    }
    if (!openList || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0 && activeIndex < items.length) {
      e.preventDefault();
      items[activeIndex].action();
    }
  };

  const groups: { key: SearchItem["group"]; label: string }[] = [
    { key: "tools", label: t("search.tools") },
    { key: "jobs", label: t("search.jobs") },
    { key: "files", label: t("search.files") },
  ];

  return (
    <ClickAwayListener onClickAway={() => setResults(null)}>
      <Box ref={anchorRef} sx={{ width: "min(480px, 100%)" }}>
        <TextField
          size="small"
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("app.searchPlaceholder")}
          inputProps={{
            role: "combobox",
            "aria-label": t("app.searchLabel"),
            "aria-expanded": openList,
            "aria-controls": openList ? LISTBOX_ID : undefined,
            "aria-activedescendant":
              activeIndex >= 0 ? items[activeIndex]?.id : undefined,
            "aria-autocomplete": "list",
            autoComplete: "off",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {busy ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={openList}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <Paper
            elevation={4}
            sx={{ width: anchorRef.current?.clientWidth, maxHeight: 420, overflow: "auto" }}
          >
            {empty && (
              <Box sx={{ p: 2, color: "text.secondary", fontSize: 14 }} role="status">
                {t("search.noResults", { q })}
              </Box>
            )}
            {items.length > 0 && (
              <Box id={LISTBOX_ID} role="listbox" aria-label={t("app.searchLabel")}>
                {groups.map((group) => {
                  const groupItems = items.filter((item) => item.group === group.key);
                  if (groupItems.length === 0) return null;
                  return (
                    <List
                      key={group.key}
                      dense
                      subheader={<ListSubheader>{group.label}</ListSubheader>}
                    >
                      {groupItems.map((item) => {
                        const index = items.indexOf(item);
                        return (
                          <ListItemButton
                            key={item.id}
                            id={item.id}
                            role="option"
                            aria-selected={index === activeIndex}
                            selected={index === activeIndex}
                            onClick={item.action}
                            onMouseMove={() => setActiveIndex(index)}
                          >
                            {item.icon}
                            <ListItemText primary={item.primary} secondary={item.secondary} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
